import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IGNORE_ERROR_HANDLING, IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import { UrlService } from '../../../../environments/urlService';
import {
  PublishedInvestorReport,
  PublishPropsDTO,
  IrDisclaimer,
  IrWithPeriodsDTO, IrValueCreationChartsPreData, IrAccountIdsDto,
} from '../pojo/ir-common-classes';
import { Observable } from 'rxjs';
import { LocalDate } from "../../../core/pojo/date-variable";
import { map } from "rxjs/operators";
import { InvestorReportFormValue } from "../pojo/ir-form-controls";
import { IrInternalService } from "./ir-internal.service";
import {
  FmvChart, FmvChartsData,
  IrFmvChartsPreData,
} from "../../organization/components/fair-value-and-pp-charts/pojo/fmv-charts-classes";
import {
  FairValueAndPpChartsOptionsService,
} from "../../organization/components/fair-value-and-pp-charts/service/charts-options-service/charts-options.service";
import { PpChartsMenuValues } from "../../organization/components/fair-value-and-pp-charts/pojo/fmv-charts-enums";
import { ValueCreationChartsDTO, ValueCreationOptions } from "../../fund-performance/dto/value-creation-charts-classes";
import {
  ValueCreationOptionsService,
} from "../component/ir/ir-sub-components/ir-value-creation-charts-wrapper/value-creation-charts-service/value-creation-options.service";


@Injectable({
  providedIn: 'root',
})
export class InvestorReportService {
  private readonly url;
  private readonly urlPortal;

  constructor(private http: HttpClient,
    private urlService: UrlService,
    private irInternalService: IrInternalService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'investor-report';
    this.urlPortal = this.urlService.getPortalUrl() + 'investor-report';
  }

  getWithPeriods(fundUUID: string, period: LocalDate | null, isAdmin: boolean): Observable<IrWithPeriodsDTO> {
    const params: any = { fundUUID, isAdmin };
    if (period) {
      params.period = period;
    }

    return this.http.get<IrWithPeriodsDTO>(this.url + '/with-periods', {
      context: new HttpContext().set(IGNORE_ERROR_HANDLING, true),
      params: params,
      withCredentials: true,
    }).pipe(
      map((response: IrWithPeriodsDTO) => this.irInternalService.processPeriodsInfo(response))
    );
  }

  create(report: InvestorReportFormValue): Observable<IrWithPeriodsDTO> {
    return this.http.post<IrWithPeriodsDTO>(this.url, report, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    }).pipe(
      map((response: IrWithPeriodsDTO) => this.irInternalService.processPeriodsInfo(response))
    );
  }

  update(report: InvestorReportFormValue): Observable<IrWithPeriodsDTO> {
    return this.http.put<IrWithPeriodsDTO>(this.url, report, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    }).pipe(
      map((response: IrWithPeriodsDTO) => this.irInternalService.processPeriodsInfo(response))
    );
  }

  checkIfExists(fundUUID: string, period: LocalDate): Observable<boolean> {
    const url = this.url + '/exists';

    return this.http.get<boolean>(url, {
      context: new HttpContext().set(IGNORE_ERROR_HANDLING, true),
      params: { fundUUID, period },
      withCredentials: true,
      responseType: 'json',
    });
  }

  getPublishedWithoutPassword(token: string): Observable<PublishedInvestorReport> {
    const headers = new HttpHeaders().append('Investor-Report-Token', token);
    const options = {
      headers,
      context: new HttpContext().set(IGNORE_ERROR_HANDLING, true),
    };
    return this.http.get<PublishedInvestorReport>(`${this.urlPortal}/token`, options);
  }

  getPublishedWithPassword(token: string, pass?: string): Promise<PublishedInvestorReport> {
    const password = { pass };
    const headers = new HttpHeaders().append('Investor-Report-Token', token);
    const options = {
      headers,
      context: new HttpContext().set(IGNORE_ERROR_HANDLING, false),
    };
    return this.http.put<PublishedInvestorReport>(this.urlPortal + '/token', password, options).toPromise();
  }

  getIrFmvChartDataWithTokenAndPassword(token: string, dto: IrFmvChartsPreData, multiplicator = 1): Observable<FmvChart> {
    const headers = new HttpHeaders().append('Investor-Report-Token', token);
    const context = new HttpContext()
      .set(IGNORE_ERROR_HANDLING, false)
      .set(IS_SPINNER_ACTIVE, false);

    const options = {
      headers,
      context,
      withCredentials: true,
    };

    return this.http.post<FmvChartsData>(`${this.urlPortal}/fmv`, dto, options).pipe(
      map((chartsData: FmvChartsData) => {
        const chart = new FmvChart();
        chart.data = chartsData;
        chart.options.fmv = FairValueAndPpChartsOptionsService.fillChartOptionsForType(
          chartsData.fmv,
          PpChartsMenuValues.fmv,
          multiplicator
        );
        return chart;
      })
    );
  }


  getValueCreationChartsWithTokenAndPassword(token: string, dto: IrValueCreationChartsPreData): Observable<ValueCreationOptions> {
    const headers = new HttpHeaders().append('Investor-Report-Token', token);
    const context = new HttpContext()
      .set(IGNORE_ERROR_HANDLING, false)
      .set(IS_SPINNER_ACTIVE, false);

    const options = {
      headers,
      context,
      withCredentials: true,
    };

    return this.http.post<ValueCreationChartsDTO>(
      `${this.urlPortal}/value-creation-charts`,
      dto,
      options
    ).pipe(
      map((data: ValueCreationChartsDTO) => ValueCreationOptionsService.getOptions(data))
    );
  }

  getDisclaimerText(token: string, pass?: string, captchaV2Token?: string, captchaV3Token?: string): Observable<IrDisclaimer> {
    const body = { pass, captchaV2Token, captchaV3Token };
    const headers = new HttpHeaders().append('Investor-Report-Token', token);
    const options = {
      headers,
      context: new HttpContext().set(IGNORE_ERROR_HANDLING, false),
    };
    return this.http.put<IrDisclaimer>(this.urlPortal + '/disclaimer', body, options);
  }

  /**
   * Get token for IR preview
   *
   * Full investor id contains orgId-fundId-accountId.
   *
   * @param fundUUID uuid of selected fund
   * @param period the period for the report
   * @param isLinkPublic determines if public page or preview
   * @param accountIdsDto contains only part with accountId
   * @returns An Observable containing the token string
   */
  getPreviewToken(fundUUID: string, period: LocalDate, isLinkPublic: boolean, accountIdsDto: IrAccountIdsDto): Observable<string> {
    const url = `${this.url}/token`;

    const params = new HttpParams()
      .set('fundUUID', fundUUID)
      .set('period', period)
      .set('isLinkPublic', isLinkPublic.toString());

    return this.http.post(url, accountIdsDto, {
      params,
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      responseType: 'text',
    });
  }

  updatePublished(reportUUID: string, published: boolean, fundUUID: string): Promise<void> {
    return this.http.put<void>(`${this.url}/published`, null, {
      params: { reportUUID, published, fundUUID },
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      responseType: 'json',
    }).toPromise();
  }

  togglePublicLink(fundUUID: string, reportUUID: string, publishProps: PublishPropsDTO): Promise<void> {
    return this.http.put<void>(`${this.url}/publicLink`, publishProps, {
      params: { fundUUID, reportUUID },
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      responseType: 'json',
    }).toPromise();
  }
}
